import * as React from 'react';

import * as ramda from 'ramda';

import {orderUsersAndTeams} from 'nutshell-core';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject, Assignable} from 'nutshell-core/types';

import {Label} from 'shells/form/label';
import {SelectableListMultiple, SelectableListSingle} from 'shells/selectable-list/selectable-list';

import styles from './quotas-form.module.css';

const SALES_TYPE_ID = '1-sales';
const MAX_QUOTAS = 6;

type Props = {
    value: string;
    schema: any;
    onValueObjectSubmit: (filters: FilterObject) => void;
    onCustomError: (errorMessage: string | React.ReactElement<any>) => void;
    onCustomErrorCleared: () => void;
};

type State = {
    currentAssigneeId: string | null | undefined;
};

export class QuotasForm extends React.Component<Props, State> {
    quotaModels: {
        id: string;
        type: string;
        name: string;
    }[];

    constructor(props: Props) {
        super(props);

        // Currently we'll set this as a local variable. Really, this should be
        // a connected component that has a selector to get this collection
        // from our store, but we're not there yet.
        this.quotaModels = this.getQuotaModels();

        const currentFilters = this.getCurrentFilters();

        this.state = {
            currentAssigneeId: currentFilters.length
                ? // @ts-expect-error
                  this.getOwnerModels(currentFilters)[0].id
                : null,
        };
    }

    render() {
        const currentFilters = this.getCurrentFilters();

        const currentAssigneeId = this.state.currentAssigneeId;
        const assigneeIsSelected = Boolean(currentAssigneeId);
        const currentAssigneeFilters = assigneeIsSelected
            ? currentFilters.filter((filter) => filter.assignee === currentAssigneeId)
            : undefined;

        // @ts-expect-error
        const ownerModels = this.getOwnerModels(currentFilters);

        // @ts-expect-error
        const currentAssignee = this.getOwnerModels(currentFilters).find(
            (assignee) => assignee.id === currentAssigneeId
        );

        return (
            <div className={styles.container}>
                <div className={styles['assignee-and-quotatypes-container']}>
                    <div className={styles['assignee-container']}>
                        <Label htmlFor='usersAndTeams' required={true}>
                            Users & teams
                        </Label>
                        <div className={styles['scroll-list']}>
                            {/* @ts-expect-error */}
                            <SelectableListSingle
                                collection={ownerModels}
                                value={currentAssigneeId}
                                onSelect={this.handleAssigneeSelect}
                            />
                        </div>
                    </div>
                    <div className={styles['list-container']}>
                        <div className={styles['quotatypes-label-container']}>
                            <Label htmlFor='quotaTypes' required={true}>
                                Quota types
                            </Label>
                            <span>{`(${currentFilters.length} / ${MAX_QUOTAS} added)`}</span>
                        </div>
                        <div className={styles['scroll-list']}>
                            {/* @ts-expect-error */}
                            <SelectableListMultiple
                                collection={
                                    currentAssignee && currentAssigneeFilters
                                        ? this.getQuotaModelsWithAssigneeNames(
                                              currentAssignee.name,
                                              // @ts-expect-error
                                              currentAssigneeFilters
                                          )
                                        : []
                                }
                                emptyStateComponent={this.getEmptyQuotaComponent()}
                                onSelect={this.handleQuotaTypeSelect}
                                // @ts-expect-error
                                value={this.getActiveAssigneeQuotaIds(currentAssigneeFilters)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    handleAssigneeSelect = (assigneeId?: string | null) => {
        this.setState({currentAssigneeId: assigneeId});
    };

    handleQuotaTypeSelect = (newAssigneeQuotaTypeIds: string[]) => {
        // Should have an assignee selected, but make sure
        const currentAssigneeId = this.state.currentAssigneeId;
        if (!currentAssigneeId) return;

        // Already at max and trying to add a new one
        const currentFilters = this.getCurrentFilters();
        const currentAssigneeQuotaTypes = currentFilters.filter(
            (filter) => filter.assignee === currentAssigneeId
        );

        // Need to check if we had max number of quotas selected, and tried to select another
        if (
            currentFilters.length === MAX_QUOTAS &&
            newAssigneeQuotaTypeIds.length > currentAssigneeQuotaTypes.length
        ) {
            this.props.onCustomError(
                <div>
                    <span>Oops, these are all the quotas as we can show in a single card.</span>
                    <br />
                    <span>If you need more, you can make a new card.</span>
                </div>
            );
        } else {
            this.props.onCustomErrorCleared();
            this.handleFilterSubmit(newAssigneeQuotaTypeIds);
        }
    };

    handleFilterSubmit = (quotaTypeIds: string[]) => {
        const currentAssigneeId = this.state.currentAssigneeId;
        if (!currentAssigneeId) return;

        const currentFilters = this.getCurrentFilters();
        const otherAssigneeFilters = currentFilters.filter((filter) => {
            return filter.assignee !== currentAssigneeId;
        });
        const currentAssigneeFilters = quotaTypeIds.map((quotaTypeId) => {
            return {
                activityType: quotaTypeId,
                assignee: currentAssigneeId,
                quotaType: quotaTypeId === SALES_TYPE_ID ? 'sales' : 'activities',
            };
        });
        const totalFilters = [...otherAssigneeFilters, ...currentAssigneeFilters];
        this.props.onValueObjectSubmit({filter: totalFilters});
    };

    getCurrentFilters = (): FilterObject[] => {
        const deparamedValues = Routing.deparam(this.props.value);

        return deparamedValues.filter || [];
    };

    getActiveAssigneeQuotaIds = (
        filters: {
            activityType: string;
        }[] = []
    ) => {
        return (
            filters
                .map((filter) => this.quotaModels.find((model) => model.id === filter.activityType))
                .filter(Boolean)
                // @ts-expect-error
                .map((quota) => quota.id)
        );
    };

    getOwnerModels(
        currentFilters: {
            assignee: string;
        }[] = []
    ) {
        const selectedCounts: {
            [id: string]: number;
        } = currentFilters.reduce<Record<string, any>>(
            (
                prev: {
                    [id: string]: number;
                },
                curr
            ) => {
                // @ts-expect-error
                prev[curr.assignee] = prev[curr.assignee] ? prev[curr.assignee] + 1 : 1;

                return prev;
            },
            {}
        );

        const selectedUserAndTeamIds = Object.keys(selectedCounts).sort((a, b) => {
            // @ts-expect-error
            if (selectedCounts[a] < selectedCounts[b]) {
                return 1;
                // @ts-expect-error
            } else if (selectedCounts[a] > selectedCounts[b]) {
                return -1;
            } else {
                return 0;
            }
        });

        // @ts-expect-error
        const usersAndTeams: Assignable[] = this.props.schema.owner.relationships
            // @ts-expect-error
            .reduce<Array<any>>((list, col) => {
                const collectionName = col.charAt(0).toUpperCase() + col.slice(1);
                const items = Nut.managedCollections[collectionName].models;

                return list.concat(items);
            }, [])
            // @ts-expect-error
            .map((model) => {
                return {
                    id: model.id,
                    type: model.get('type'),
                    name: model.get('name'),
                    initials: model.get('initials'),
                    avatarUrl: model.get('avatarUrl'),
                    isEnabled: model.get('type') === 'users' ? model.get('isEnabled') : true,
                };
            });

        const orderedUsersAndTeams = orderUsersAndTeams(usersAndTeams, selectedUserAndTeamIds);

        const usersAndTeamsWithCounts = orderedUsersAndTeams.map((assignable) => {
            const newAssignable: any = ramda.clone(assignable);
            newAssignable.numSelected = selectedCounts[assignable.id];

            return newAssignable;
        });

        return usersAndTeamsWithCounts;
    }

    getQuotaModels() {
        const salesQuotaItem = {
            id: SALES_TYPE_ID,
            type: 'activityTypes',
            name: 'Sales',
        } as const;

        return [salesQuotaItem].concat(
            // @ts-expect-error
            this.props.schema.activityType.relationships
                // @ts-expect-error
                .reduce<Array<any>>((list, col) => {
                    const collectionName = col.charAt(0).toUpperCase() + col.slice(1);
                    const items = Nut.managedCollections[collectionName].models;
                    // @ts-expect-error
                    const benchmarkableItems = items.filter((item) => {
                        return item.get('isBenchmarkable') === true;
                    });

                    return list.concat(benchmarkableItems);
                }, [])
                // @ts-expect-error
                .map((model) => {
                    return {
                        id: model.id,
                        type: model.get('type'),
                        name: model.get('name'),
                    };
                })
        );
    }

    getQuotaModelsWithAssigneeNames = (
        assigneeName: string,
        currentAssigneeFilters: {
            activityType: string;
        }[]
    ) => {
        const selectedQuotas = currentAssigneeFilters.map((filter) => {
            return filter.activityType;
        });

        const quotaModelsWithNames = this.quotaModels.map((model) => {
            return {...model, name: `${model.name} (${assigneeName})`};
        });

        // @ts-expect-error
        const selectedQuotaModels = [];
        // @ts-expect-error
        const unselectedQuotaModels = [];

        quotaModelsWithNames.forEach((quota) => {
            if (selectedQuotas.includes(quota.id)) {
                selectedQuotaModels.push(quota);
            } else {
                unselectedQuotaModels.push(quota);
            }
        });

        // @ts-expect-error
        return selectedQuotaModels.concat(unselectedQuotaModels);
    };

    getEmptyQuotaComponent() {
        return <div>Select a person or team on the left to view their quotas</div>;
    }
}
